import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Tag, X } from "lucide-react";
import { API, formatApiError, startingTotal } from "@/lib/content";
import { inputCls } from "./ServiceChips";

export const PromoEstimate = ({ services, promo, setPromo, prefix }) => {
  const [code, setCode] = useState(promo?.code ?? "");
  const [err, setErr] = useState("");
  useEffect(() => { setCode(promo?.code ?? ""); }, [promo]);

  const apply = async () => {
    if (!code.trim()) return;
    setErr("");
    try {
      const { data } = await axios.get(`${API}/promos/${encodeURIComponent(code.trim())}`);
      setPromo(data);
    } catch (e) {
      setPromo(null);
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const sub = startingTotal(services);
  const off = promo ? Math.round((sub * promo.percent) / 100) : 0;

  return (
    <div className="grid gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:grid-cols-2" data-testid={`${prefix}-promo-estimate`}>
      <div>
        <span className="mb-2 block font-mono-x text-[11px] uppercase tracking-[0.2em] text-slate-400">Promo code</span>
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }} placeholder="e.g. NEWCITRINE" className={`${inputCls} uppercase`} data-testid={`${prefix}-promo-input`} />
          <button type="button" onClick={apply} data-testid={`${prefix}-promo-apply`} className="shrink-0 rounded-xl border border-amber-300/60 px-5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-400 hover:text-[#0B0F17]">Apply</button>
        </div>
        <AnimatePresence>
          {promo && (
            <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-400/10 px-3 py-1 text-sm text-emerald-300" data-testid={`${prefix}-promo-applied`}>
              <Tag className="h-3.5 w-3.5" /> {promo.code} · {promo.label}
              <button type="button" onClick={() => { setPromo(null); setCode(""); }} aria-label="Remove code" data-testid={`${prefix}-promo-remove`}><X className="h-3.5 w-3.5" /></button>
            </motion.p>
          )}
        </AnimatePresence>
        {err && <p className="mt-3 text-sm text-red-400" data-testid={`${prefix}-promo-error`}>{err}</p>}
      </div>
      <div className="md:text-right">
        <span className="mb-2 block font-mono-x text-[11px] uppercase tracking-[0.2em] text-slate-400">Starting estimate</span>
        {sub ? (
          <>
            {off > 0 && <p className="text-sm text-slate-400 line-through">${sub}</p>}
            <p className="whitespace-nowrap font-display text-3xl font-extrabold text-amber-300 sm:text-4xl" data-testid={`${prefix}-estimate-total`}>from ${sub - off}</p>
            <p className="mt-1 text-xs text-slate-400">Final price confirmed after we learn your home's size & condition.</p>
          </>
        ) : (
          <p className="text-sm text-slate-400">Pick services to see a starting price.</p>
        )}
      </div>
    </div>
  );
};
